import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { 
  Archive as ArchiveIcon,
  Search, 
  Briefcase,
  FileCheck,
  Calendar,
  Building,
  Phone,
  Mail,
  MapPin,
  Filter,
  RotateCcw,
  Eye,
  Download,
  FolderOpen,
  Clock,
  DollarSign,
  ChevronRight,
  LayoutGrid,
  TableIcon,
  ArrowUp,
  ArrowDown,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { parseLocalDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Archive() {
  const [activeTab, setActiveTab] = useState('jobs');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  
  // View mode: 'table' (List view, default) or 'cards' (Tile view).
  // Persisted the same way as the Jobs/Proposals toggle — saved choice
  // always wins over the default.
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('archiveViewMode') || 'table';
  });
  useEffect(() => {
    localStorage.setItem('archiveViewMode', viewMode);
  }, [viewMode]);

  // Table view pagination — 100 rows/page, client-side over the already
  // search/date-filtered list. Resets to page 1 on tab/search/date change.
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [activeTab, search, dateFilter]);
  const PAGE_SIZE = 100;

  // List-view-only sort — Job Date header is the only sortable column,
  // defaults to newest first, flips asc/desc on click.
  const [sortOrder, setSortOrder] = useState('desc');

  // Data states
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedProposals, setArchivedProposals] = useState([]);
  
  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewType, setPreviewType] = useState('job');

  useEffect(() => {
    fetchArchivedData();
  }, []);

  const fetchArchivedData = async () => {
    setLoading(true);
    try {
      // Fetch archived jobs using dedicated endpoint
      const jobsRes = await axios.get(`${API}/jobs/archived`);
      setArchivedJobs(jobsRes.data);

      // Fetch archived/rejected proposals using dedicated endpoint
      const proposalsRes = await axios.get(`${API}/proposals/archived/list`);
      setArchivedProposals(proposalsRes.data);
    } catch (error) {
      console.error('Failed to fetch archived data:', error);
      toast.error('Failed to load archive');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreJob = async (id) => {
    try {
      await axios.put(`${API}/jobs/${id}/restore`);
      toast.success('Job restored - status set to Pending for review');
      fetchArchivedData();
    } catch (error) {
      toast.error('Failed to restore job');
    }
  };

  const handleRestoreProposal = async (id) => {
    try {
      await axios.put(`${API}/proposals/${id}/restore`);
      toast.success('Proposal restored to Draft status');
      fetchArchivedData();
    } catch (error) {
      toast.error('Failed to restore proposal');
    }
  };

  // Unarchive an Accepted-Closed proposal — unlike Restore above, status
  // is untouched (stays Accepted), it just moves back to the active list.
  const handleUnarchiveProposal = async (id) => {
    try {
      await axios.put(`${API}/proposals/${id}/unarchive`);
      toast.success('Proposal moved back to active');
      fetchArchivedData();
    } catch (error) {
      toast.error('Failed to unarchive proposal');
    }
  };

  const handleUnarchive = async (type, id) => {
    if (type === 'job') {
      await handleRestoreJob(id);
    } else {
      // Sep-2026: every archived proposal status (Accepted, Completed,
      // Lost) now unarchives status-preserving — Restore-to-Draft
      // (handleRestoreProposal) is no longer wired to this dispatcher,
      // kept defined but unreferenced per the approved decision.
      await handleUnarchiveProposal(id);
    }
  };

  const handlePreview = (item, type) => {
    setPreviewItem(item);
    setPreviewType(type);
    setShowPreview(true);
  };

  // Filter by date range
  const filterByDate = useCallback((items, dateField = 'created_at') => {
    if (dateFilter === 'all') return items;
    
    const now = new Date();
    const cutoff = new Date();
    
    switch (dateFilter) {
      case '30days':
        cutoff.setDate(now.getDate() - 30);
        break;
      case '90days':
        cutoff.setDate(now.getDate() - 90);
        break;
      case '6months':
        cutoff.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        cutoff.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return items;
    }
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= cutoff;
    });
  }, [dateFilter]);

  // Search and filter
  const filteredJobs = useMemo(() => {
    let filtered = archivedJobs;
    
    // Apply date filter
    filtered = filterByDate(filtered);
    
    // Apply search — comma-split into pieces, every piece must match (AND),
    // each piece checked as a whole phrase across all fields (OR), same
    // pattern as the Proposals page / Jobs page backend search.
    if (search) {
      const searchPieces = search.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      filtered = filtered.filter(job => searchPieces.every(term =>
        job.job_number?.toLowerCase().includes(term) ||
        job.client_name?.toLowerCase().includes(term) ||
        job.contact_name?.toLowerCase().includes(term) ||
        job.service_type?.toLowerCase().includes(term) ||
        job.service_address?.toLowerCase().includes(term) ||
        job.date?.toLowerCase().includes(term)
      ));
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [archivedJobs, search, filterByDate]);

  // List-view-only re-sort of filteredJobs by Job Date (field `date`).
  // Tile view keeps using `filteredJobs` above (created_at order) untouched.
  const sortedJobsForTable = useMemo(() => {
    const list = [...filteredJobs];
    list.sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });
    return list;
  }, [filteredJobs, sortOrder]);

  const handleJobDateSort = () => {
    setSortOrder(o => (o === 'desc' ? 'asc' : 'desc'));
    setPage(1);
  };

  const filteredProposals = useMemo(() => {
    let filtered = archivedProposals;
    
    // Apply date filter
    filtered = filterByDate(filtered);
    
    // Apply search — same comma-split AND logic as filteredJobs above.
    // `created_at` is the date field these rows display (the "Date" column).
    if (search) {
      const searchPieces = search.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      filtered = filtered.filter(prop => searchPieces.every(term =>
        prop.proposal_number?.toLowerCase().includes(term) ||
        prop.client_name?.toLowerCase().includes(term) ||
        prop.contact_name?.toLowerCase().includes(term) ||
        prop.proposal_type?.toLowerCase().includes(term) ||
        prop.created_at?.toLowerCase().includes(term)
      ));
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [archivedProposals, search, filterByDate]);

  // /archived/list returns every MANUALLY archived proposal (Lost or
  // Completed) in one array — split by status for the tabs below.
  // Accepted is no longer archivable directly (2026-09-01: the old
  // "Accepted Closed" tab was removed, confirmed empty in the database —
  // a proposal must become Lost or Completed before it can be archived).
  const filteredLostProposals = useMemo(
    () => filteredProposals.filter(p => p.status === 'Lost'),
    [filteredProposals]
  );
  const filteredCompletedProposals = useMemo(
    () => filteredProposals.filter(p => p.status === 'Completed'),
    [filteredProposals]
  );

  // isProposal scopes the "Completed" → "Closed" label rename (#44) to
  // Proposals ONLY — Jobs have their own unrelated "Completed" status
  // (e.g. the "Completed Jobs" tab) that must NOT be relabeled here.
  const getStatusBadge = (status, isProposal = false) => {
    const colors = {
      'Completed': 'bg-emerald-100 text-emerald-700',
      'Accepted': 'bg-emerald-100 text-emerald-700',
      'Lost': 'bg-red-100 text-red-700',
    };
    const label = isProposal && status === 'Completed' ? 'Closed' : status;
    return <Badge className={colors[status] || 'bg-slate-100 text-slate-700'}>{label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Table view helpers — client-side paging of an already-filtered list
  const pageSlice = (list) => list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const renderPagination = (list) => {
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    if (list.length === 0) return null;
    return (
      <div className="flex items-center justify-between pt-2" data-testid="archive-pagination">
        <Button
          variant="outline"
          size="sm"
          className="border-slate-200"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          data-testid="archive-prev-page-btn"
        >
          Prev
        </Button>
        <span className="text-sm text-slate-500" data-testid="archive-page-indicator">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="border-slate-200"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          data-testid="archive-next-page-btn"
        >
          Next
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] flex items-center gap-3">
            <ArchiveIcon className="w-8 h-8 text-amber-500" />
            Archive Center
          </h1>
          <p className="text-slate-500 mt-1">Browse and search completed jobs and proposals</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <FolderOpen className="w-4 h-4" />
            <span>{filteredJobs.length} Jobs</span>
            <span className="text-slate-300">|</span>
            <span>{filteredLostProposals.length + filteredCompletedProposals.length} Proposals</span>
          </div>
          {/* View Toggle — same icons/order/placement/persistence as Jobs & Proposals. List (table) is now the default. */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
              data-testid="archive-view-table-btn"
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
              data-testid="archive-view-cards-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, number, client, address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-50"
                data-testid="archive-search-input"
              />
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]" data-testid="archive-date-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="1year">Last Year</SelectItem>
              </SelectContent>
            </Select>
            {(search || dateFilter !== 'all') && (
              <Button 
                variant="outline" 
                onClick={() => { setSearch(''); setDateFilter('all'); }}
                className="shrink-0"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="jobs" className="data-[state=active]:bg-white" data-testid="archive-jobs-tab">
            <Briefcase className="w-4 h-4 mr-2" />
            Completed Jobs ({filteredJobs.length})
          </TabsTrigger>
          <TabsTrigger value="proposals" className="data-[state=active]:bg-white" data-testid="archive-proposals-tab">
            <FileCheck className="w-4 h-4 mr-2" />
            Lost Proposals ({filteredLostProposals.length})
          </TabsTrigger>
          <TabsTrigger value="completed-proposals" className="data-[state=active]:bg-white" data-testid="archive-completed-proposals-tab">
            <FileCheck className="w-4 h-4 mr-2" />
            Completed Proposals ({filteredCompletedProposals.length})
          </TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="space-y-3">
          {filteredJobs.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No completed jobs found</p>
                {search && <p className="text-sm text-slate-400 mt-2">Try adjusting your search</p>}
              </CardContent>
            </Card>
          ) : viewMode === 'table' ? (
            <>
              <Card className="border-slate-200">
                <div className="p-3 overflow-x-auto">
                  <Table data-testid="archive-jobs-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Service Type</TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:text-slate-900"
                          onClick={handleJobDateSort}
                          data-testid="archive-jobs-sort-date"
                        >
                          <span className="flex items-center gap-1">
                            Job Date
                            {sortOrder === 'desc'
                              ? <ArrowDown className="w-3 h-3" />
                              : <ArrowUp className="w-3 h-3" />}
                          </span>
                        </TableHead>
                        <TableHead>Service Address</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageSlice(sortedJobsForTable).map(job => (
                        <TableRow
                          key={job.id}
                          className="cursor-pointer"
                          onClick={() => handlePreview(job, 'job')}
                          data-testid={`archive-job-row-${job.job_number}`}
                        >
                          <TableCell className="font-mono text-amber-600 font-bold whitespace-nowrap">{job.job_number}</TableCell>
                          <TableCell className="text-slate-900">{job.client_name}</TableCell>
                          <TableCell className="text-slate-500">{job.contact_name || '—'}</TableCell>
                          <TableCell className="text-slate-500">{job.service_type || '—'}</TableCell>
                          <TableCell className="text-slate-500 whitespace-nowrap">{job.date ? format(parseLocalDate(job.date), 'MMM d, yyyy') : '—'}</TableCell>
                          <TableCell className="text-slate-500 truncate max-w-[220px]">{job.service_address || '—'}</TableCell>
                          <TableCell>{getStatusBadge(job.status)}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                                onClick={(e) => { e.stopPropagation(); handleRestoreJob(job.id); }}
                                data-testid={`restore-job-table-${job.job_number}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handlePreview(job, 'job'); }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Link to={`/jobs/${job.id}`} onClick={(e) => e.stopPropagation()}>
                                <Button variant="outline" size="sm">
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              {renderPagination(sortedJobsForTable)}
            </>
          ) : (
            <div className="grid gap-3">
              {filteredJobs.map(job => (
                <Card 
                  key={job.id} 
                  className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
                  onClick={() => handlePreview(job, 'job')}
                  data-testid={`archive-job-${job.job_number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-amber-600 font-bold">{job.job_number}</span>
                          {getStatusBadge(job.status)}
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate">{job.client_name}</h3>
                        {job.contact_name && (
                          <p className="text-sm text-slate-500">{job.contact_name}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                          {job.service_type && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {job.service_type}
                            </span>
                          )}
                          {job.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(parseLocalDate(job.date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {job.service_address && (
                            <span className="flex items-center gap-1 truncate max-w-[200px]">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {job.service_address}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={(e) => { e.stopPropagation(); handleRestoreJob(job.id); }}
                          data-testid={`restore-job-${job.job_number}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePreview(job, 'job'); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Link to={`/jobs/${job.id}`} onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Proposals Tab */}
        <TabsContent value="proposals" className="space-y-3">
          {filteredLostProposals.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No lost proposals found</p>
                {search && <p className="text-sm text-slate-400 mt-2">Try adjusting your search</p>}
              </CardContent>
            </Card>
          ) : viewMode === 'table' ? (
            <>
              <Card className="border-slate-200">
                <div className="p-3 overflow-x-auto">
                  <Table data-testid="archive-lost-proposals-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proposal #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Lost Reason</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageSlice(filteredLostProposals).map(proposal => (
                        <TableRow
                          key={proposal.id}
                          className="cursor-pointer"
                          onClick={() => handlePreview(proposal, 'proposal')}
                          data-testid={`archive-lost-proposal-row-${proposal.proposal_number}`}
                        >
                          <TableCell className="font-mono text-amber-600 font-bold whitespace-nowrap">
                            {proposal.proposal_number}
                            {proposal.job_number && (
                              <Badge variant="outline" className="text-xs ml-2">→ {proposal.job_number}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-900">{proposal.client_name}</TableCell>
                          <TableCell className="text-slate-500">{proposal.contact_name || '—'}</TableCell>
                          <TableCell className="text-slate-500">{proposal.proposal_type || '—'}</TableCell>
                          <TableCell className="text-emerald-600 font-medium whitespace-nowrap">{proposal.total ? formatCurrency(proposal.total) : '—'}</TableCell>
                          <TableCell className="text-slate-500 whitespace-nowrap">{proposal.created_at ? format(new Date(proposal.created_at), 'MMM d, yyyy') : '—'}</TableCell>
                          <TableCell>{getStatusBadge(proposal.status, true)}</TableCell>
                          <TableCell className="text-slate-400 italic truncate max-w-[180px]" title={proposal.lost_reason}>
                            {proposal.lost_reason ? `"${proposal.lost_reason}"` : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                onClick={(e) => { e.stopPropagation(); handleUnarchiveProposal(proposal.id); }}
                                title="Unarchive"
                                data-testid={`unarchive-lost-proposal-table-${proposal.id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Unarchive
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handlePreview(proposal, 'proposal'); }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Link to={`/proposals/${proposal.id}/preview`} onClick={(e) => e.stopPropagation()}>
                                <Button variant="outline" size="sm">
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              {renderPagination(filteredLostProposals)}
            </>
          ) : (
            <div className="grid gap-3">
              {filteredLostProposals.map(proposal => (
                <Card 
                  key={proposal.id} 
                  className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
                  onClick={() => handlePreview(proposal, 'proposal')}
                  data-testid={`archive-proposal-${proposal.proposal_number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-amber-600 font-bold">{proposal.proposal_number}</span>
                          {getStatusBadge(proposal.status, true)}
                          {proposal.status === 'Lost' && proposal.lost_reason && (
                            <span
                              className="text-xs text-slate-400 italic truncate max-w-[220px]"
                              title={proposal.lost_reason}
                              data-testid={`archive-lost-reason-${proposal.id}`}
                            >
                              "{proposal.lost_reason}"
                            </span>
                          )}
                          {proposal.job_number && (
                            <Badge variant="outline" className="text-xs">
                              → {proposal.job_number}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate">{proposal.client_name}</h3>
                        {proposal.contact_name && (
                          <p className="text-sm text-slate-500">{proposal.contact_name}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                          {proposal.proposal_type && (
                            <span className="flex items-center gap-1">
                              <FileCheck className="w-3 h-3" />
                              {proposal.proposal_type}
                            </span>
                          )}
                          {proposal.total && (
                            <span className="flex items-center gap-1 font-medium text-emerald-600">
                              <DollarSign className="w-3 h-3" />
                              {formatCurrency(proposal.total)}
                            </span>
                          )}
                          {proposal.created_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(proposal.created_at), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          onClick={(e) => { e.stopPropagation(); handleUnarchiveProposal(proposal.id); }}
                          title="Unarchive"
                          data-testid={`unarchive-lost-proposal-${proposal.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Unarchive
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePreview(proposal, 'proposal'); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Link to={`/proposals/${proposal.id}/preview`} onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Completed Proposals Tab — manually archived Completed
            proposals. Status stays "Completed" throughout; Unarchive
            is status-preserving, same pattern as Accepted Closed. */}
        <TabsContent value="completed-proposals" className="space-y-3">
          {filteredCompletedProposals.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No completed proposals found</p>
                {search && <p className="text-sm text-slate-400 mt-2">Try adjusting your search</p>}
              </CardContent>
            </Card>
          ) : viewMode === 'table' ? (
            <>
              <Card className="border-slate-200">
                <div className="p-3 overflow-x-auto">
                  <Table data-testid="archive-completed-proposals-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proposal #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageSlice(filteredCompletedProposals).map(proposal => (
                        <TableRow
                          key={proposal.id}
                          className="cursor-pointer"
                          onClick={() => handlePreview(proposal, 'proposal')}
                          data-testid={`archive-completed-proposal-row-${proposal.proposal_number}`}
                        >
                          <TableCell className="font-mono text-amber-600 font-bold whitespace-nowrap">
                            {proposal.proposal_number}
                            {proposal.job_number && (
                              <Badge variant="outline" className="text-xs ml-2">→ {proposal.job_number}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-900">{proposal.client_name}</TableCell>
                          <TableCell className="text-slate-500">{proposal.contact_name || '—'}</TableCell>
                          <TableCell className="text-slate-500">{proposal.proposal_type || '—'}</TableCell>
                          <TableCell className="text-emerald-600 font-medium whitespace-nowrap">{proposal.total ? formatCurrency(proposal.total) : '—'}</TableCell>
                          <TableCell className="text-slate-500 whitespace-nowrap">{proposal.created_at ? format(new Date(proposal.created_at), 'MMM d, yyyy') : '—'}</TableCell>
                          <TableCell>{getStatusBadge(proposal.status, false)}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                                onClick={(e) => { e.stopPropagation(); handleUnarchiveProposal(proposal.id); }}
                                data-testid={`unarchive-completed-proposal-table-${proposal.id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Unarchive
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handlePreview(proposal, 'proposal'); }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Link to={`/proposals/${proposal.id}/preview`} onClick={(e) => e.stopPropagation()}>
                                <Button variant="outline" size="sm">
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              {renderPagination(filteredCompletedProposals)}
            </>
          ) : (
            <div className="grid gap-3">
              {filteredCompletedProposals.map(proposal => (
                <Card
                  key={proposal.id}
                  className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
                  onClick={() => handlePreview(proposal, 'proposal')}
                  data-testid={`archive-completed-proposal-${proposal.proposal_number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-amber-600 font-bold">{proposal.proposal_number}</span>
                          {/* isProposal=false here on purpose — this tab is
                              literally named "Completed Proposals", so the
                              badge should read "Completed" too, not the
                              "Closed" relabel used elsewhere (#44). */}
                          {getStatusBadge(proposal.status, false)}
                          {proposal.job_number && (
                            <Badge variant="outline" className="text-xs">
                              → {proposal.job_number}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate">{proposal.client_name}</h3>
                        {proposal.contact_name && (
                          <p className="text-sm text-slate-500">{proposal.contact_name}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                          {proposal.proposal_type && (
                            <span className="flex items-center gap-1">
                              <FileCheck className="w-3 h-3" />
                              {proposal.proposal_type}
                            </span>
                          )}
                          {proposal.total && (
                            <span className="flex items-center gap-1 font-medium text-emerald-600">
                              <DollarSign className="w-3 h-3" />
                              {formatCurrency(proposal.total)}
                            </span>
                          )}
                          {proposal.created_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(proposal.created_at), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={(e) => { e.stopPropagation(); handleUnarchiveProposal(proposal.id); }}
                          data-testid={`unarchive-completed-proposal-${proposal.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Unarchive
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePreview(proposal, 'proposal'); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Link to={`/proposals/${proposal.id}/preview`} onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {previewItem && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-mono text-amber-600 font-bold">
                      {previewType === 'job' ? previewItem.job_number : previewItem.proposal_number}
                    </p>
                    <DialogTitle className="text-xl font-bold mt-1">
                      {previewItem.client_name}
                    </DialogTitle>
                  </div>
                  {getStatusBadge(previewItem.status, previewType === 'proposal')}
                </div>
              </DialogHeader>

              <div className="space-y-4 pt-4">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <Building className="w-4 h-4" />
                      Contact
                    </h4>
                    {previewItem.contact_name && (
                      <p className="font-medium">{previewItem.contact_name}</p>
                    )}
                    {(previewItem.phone || previewItem.client_phone) && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" />
                        {previewItem.phone || previewItem.client_phone}
                      </p>
                    )}
                    {(previewItem.email || previewItem.client_email) && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {previewItem.email || previewItem.client_email}
                      </p>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {previewType === 'job' ? 'Service Address' : 'Address'}
                    </h4>
                    <p className="text-sm">
                      {previewType === 'job' 
                        ? previewItem.service_address || 'Not specified'
                        : [previewItem.client_street, previewItem.client_city, previewItem.client_state, previewItem.client_zip].filter(Boolean).join(', ') || 'Not specified'
                      }
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Type:</span>
                      <span className="ml-2 font-medium">
                        {previewType === 'job' ? previewItem.service_type : previewItem.proposal_type}
                      </span>
                    </div>
                    {previewType === 'job' && previewItem.date && (
                      <div>
                        <span className="text-slate-500">Date:</span>
                        <span className="ml-2 font-medium">
                          {format(parseLocalDate(previewItem.date), 'MMM d, yyyy')}
                        </span>
                      </div>
                    )}
                    {previewType === 'proposal' && previewItem.total && (
                      <div>
                        <span className="text-slate-500">Total:</span>
                        <span className="ml-2 font-medium text-emerald-600">
                          {formatCurrency(previewItem.total)}
                        </span>
                      </div>
                    )}
                    {previewItem.created_at && (
                      <div>
                        <span className="text-slate-500">Created:</span>
                        <span className="ml-2 font-medium">
                          {format(new Date(previewItem.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {previewItem.notes && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2">Notes</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{previewItem.notes}</p>
                  </div>
                )}

                {/* Files */}
                {((previewType === 'job' && previewItem.files?.length > 0) || 
                  (previewType === 'proposal' && previewItem.attachments?.length > 0)) && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2">
                      Attachments ({previewType === 'job' ? previewItem.files?.length : previewItem.attachments?.length})
                    </h4>
                    <div className="space-y-2">
                      {(previewType === 'job' ? previewItem.files : previewItem.attachments)?.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                          <span className="text-sm truncate">{file.filename}</span>
                          <Button variant="ghost" size="sm">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleUnarchive(previewType, previewItem.id)}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore to Active
                  </Button>
                  <Link to={previewType === 'job' ? `/jobs/${previewItem.id}` : `/proposals/${previewItem.id}/preview`}>
                    <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                      View Full Details
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
